/**
 * SystemStatusContext — exposes a small reactive snapshot of the
 * platform's "live" state to the taskbar and other status surfaces.
 *
 * The data is intentionally lightweight (counters + one liner messages)
 * so the bar stays informative without polling expensive endpoints.
 * In demo mode we synthesise plausible values; in production the
 * fields come from ``GET /system/status`` (a future endpoint).
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "../auth/AuthContext";

export interface SystemStatus {
  apiOnline: boolean;
  ingestaPerHour: number;
  alertasAbiertas: number;
  ordenesFueraSla: number;
  nrwUltimoPeriodo: string;
  lastUpdated: Date;
}

const SystemStatusContext = createContext<SystemStatus | null>(null);

export function SystemStatusProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { isDemo, user } = useAuth();
  const [status, setStatus] = useState<SystemStatus>(() => seed(isDemo));

  useEffect(() => {
    if (!user) return undefined;
    // Refresh every 15s to mimic a live status feed.
    const id = window.setInterval(() => {
      setStatus((prev) => tick(prev));
    }, 15_000);
    return () => window.clearInterval(id);
  }, [user]);

  const value = useMemo(() => status, [status]);

  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
}

export function useSystemStatus(): SystemStatus {
  const ctx = useContext(SystemStatusContext);
  if (ctx === null) {
    throw new Error(
      "useSystemStatus must be used within a <SystemStatusProvider>",
    );
  }
  return ctx;
}

function seed(isDemo: boolean): SystemStatus {
  return {
    apiOnline: true,
    ingestaPerHour: isDemo ? 1_240_000 : 0,
    alertasAbiertas: isDemo ? 37 : 0,
    ordenesFueraSla: isDemo ? 3 : 0,
    nrwUltimoPeriodo: "2025-01",
    lastUpdated: new Date(),
  };
}

function tick(prev: SystemStatus): SystemStatus {
  // Small jitter so the taskbar visibly refreshes.
  const drift = Math.round((Math.random() - 0.5) * 20_000);
  return {
    ...prev,
    ingestaPerHour: Math.max(0, prev.ingestaPerHour + drift),
    lastUpdated: new Date(),
  };
}
